import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  hasFcmVapidKey,
  initFirebaseMessaging,
  listenForServiceWorkerMessages,
  logFcmToken,
  logNotificationPayload,
  parseFcmPayload,
  requestNotificationPermissionAndToken,
} from "../firebaseMessaging";
import { playNotificationSound } from "../utils/notificationAlert";
import { registerAdminDeviceInBackground } from "../services/registerDeviceService";
import { isAdminLoggedIn } from "../utils/deviceInfo";
import { isUserApprovalNotification } from "../utils/notificationRoutes";

const STORAGE_KEY = "adminNotifications";
const MAX_STORED = 50;

const NotificationContext = createContext(null);

function loadStoredNotifications() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveStoredNotifications(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
  } catch {
    /* ignore quota errors */
  }
}

function formatTimeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function createNotificationFromPayload(payload) {
  const parsed = parseFcmPayload(payload);
  const id = payload?.messageId || payload?.data?.id || `n_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  return {
    id,
    title: parsed.title,
    body: parsed.body,
    type: parsed.type || parsed.data?.type || null,
    link: parsed.link,
    data: parsed.data,
    read: false,
    createdAt: new Date().toISOString(),
  };
}

export function NotificationProvider({ children }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(loadStoredNotifications);
  const [fcmToken, setFcmToken] = useState(null);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [fcmStatus, setFcmStatus] = useState("idle");
  const [fcmError, setFcmError] = useState("");
  const [bellBlinking, setBellBlinking] = useState(false);
  const hasVapidKey = hasFcmVapidKey();
  const blinkTimerRef = useRef(null);

  const triggerAlert = useCallback(() => {
    playNotificationSound();
    setBellBlinking(true);
    if (blinkTimerRef.current) clearTimeout(blinkTimerRef.current);
    blinkTimerRef.current = setTimeout(() => setBellBlinking(false), 5000);
  }, []);

  const clearBellAlert = useCallback(() => {
    setBellBlinking(false);
    if (blinkTimerRef.current) {
      clearTimeout(blinkTimerRef.current);
      blinkTimerRef.current = null;
    }
  }, []);

  const addNotification = useCallback(
    (item, { alert = false } = {}) => {
      setNotifications((prev) => {
        const next = [item, ...prev.filter((n) => n.id !== item.id)].slice(0, MAX_STORED);
        saveStoredNotifications(next);
        return next;
      });
      if (alert) triggerAlert();
    },
    [triggerAlert]
  );

  const pushFromFcm = useCallback(
    (payload) => {
      logNotificationPayload(payload, "Notification UI");
      const item = createNotificationFromPayload(payload);
      console.log("[Notification] Parsed for UI:", item);
      addNotification(item, { alert: true });
    },
    [addNotification]
  );

  useEffect(
    () => () => {
      if (blinkTimerRef.current) clearTimeout(blinkTimerRef.current);
    },
    []
  );

  useEffect(() => {
    saveStoredNotifications(notifications);
  }, [notifications]);

  // Re-register on session load only when FCM token already exists; otherwise wait for FCM init / enable
  useEffect(() => {
    if (isAdminLoggedIn() && localStorage.getItem("fcmToken")) {
      registerAdminDeviceInBackground(localStorage.getItem("fcmToken"), { skipIfNoToken: true });
    }
  }, []);

  const syncDeviceRegistration = useCallback((token) => {
    if (token) registerAdminDeviceInBackground(token, { skipIfNoToken: true });
  }, []);

  useEffect(() => {
    let active = true;

    const unlistenSw = listenForServiceWorkerMessages(
      (payload) => {
        if (active) pushFromFcm(payload);
      },
      (path) => {
        if (!active || !path) return;
        if (path.startsWith("http")) {
          window.location.href = path;
          return;
        }
        navigate(path.startsWith("/") ? path : `/${path}`);
      }
    );

    (async () => {
      setFcmStatus("loading");
      if (!hasVapidKey) {
        setFcmError("Add REACT_APP_FIREBASE_VAPID_KEY to .env and restart npm run start.");
        setFcmStatus("ready");
        return;
      }

      try {
        const result = await initFirebaseMessaging((payload) => {
          if (active) pushFromFcm(payload);
        });

        if (!active) return;

        if (!result.supported) {
          setFcmStatus("unsupported");
          setFcmError("Push notifications are not supported in this browser.");
          return;
        }

        if (result.token) {
          setFcmToken(result.token);
          localStorage.setItem("fcmToken", result.token);
          logFcmToken(result.token, "FCM app load");
          syncDeviceRegistration(result.token);
        }

        setPermission(typeof Notification !== "undefined" ? Notification.permission : "default");
        setFcmStatus("ready");
      } catch (e) {
        if (!active) return;
        setFcmStatus("ready");
        setFcmError(e?.message || "Failed to initialize notifications.");
      }
    })();

    return () => {
      active = false;
      unlistenSw();
    };
  }, [pushFromFcm, hasVapidKey, syncDeviceRegistration, navigate]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const userApprovalUnreadCount = useMemo(
    () => notifications.filter((n) => !n.read && isUserApprovalNotification(n)).length,
    [notifications]
  );

  const markAsRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    saveStoredNotifications([]);
  }, []);

  const enablePush = useCallback(async () => {
    setFcmError("");
    setFcmStatus("loading");
    try {
      const result = await requestNotificationPermissionAndToken();
      setPermission(result.permission || Notification.permission);
      if (!result.ok) {
        if (result.reason === "denied") {
          setFcmError("Notification permission was denied.");
        } else if (result.reason === "missing_vapid_key") {
          setFcmError("Add REACT_APP_FIREBASE_VAPID_KEY to .env and restart npm run start.");
        } else if (result.reason === "token_empty") {
          setFcmError("FCM token was empty. Check console for errors and verify VAPID key in Firebase Console.");
        } else {
          setFcmError("Push notifications are not supported in this browser.");
        }
        setFcmStatus("ready");
        return false;
      }
      setFcmToken(result.token);
      localStorage.setItem("fcmToken", result.token);
      logFcmToken(result.token, "FCM permission granted");
      syncDeviceRegistration(result.token);
      setFcmStatus("ready");
      return true;
    } catch (e) {
      const msg = e?.message || "Failed to enable push notifications.";
      console.error("[FCM] enablePush error:", e);
      setFcmError(msg);
      setFcmStatus("ready");
      return false;
    }
  }, [syncDeviceRegistration]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      userApprovalUnreadCount,
      fcmToken,
      permission,
      fcmStatus,
      fcmError,
      hasVapidKey,
      formatTimeAgo,
      addNotification,
      markAsRead,
      markAllRead,
      clearAll,
      enablePush,
      bellBlinking,
      clearBellAlert,
    }),
    [
      notifications,
      unreadCount,
      userApprovalUnreadCount,
      fcmToken,
      permission,
      fcmStatus,
      fcmError,
      hasVapidKey,
      bellBlinking,
      addNotification,
      markAsRead,
      markAllRead,
      clearAll,
      enablePush,
      clearBellAlert,
    ]
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
}
